import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { JobPaymentSummary } from '@/types/jobPaymentTypes';
import {
  formatBillingLabel,
  formatClientPaymentStatus,
  formatPaymentMethod,
} from '@/utils/jobPaymentSummary';

type JobPaymentSummarySectionProps = {
  summary: JobPaymentSummary;
  variant: 'client' | 'worker';
  showBilling?: boolean;
  showOpsDebug?: boolean;
  className?: string;
};

function SummaryField({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) {
  return (
    <div>
      <div className="text-sm text-black font-semibold mb-1">{label}</div>
      <div className="font-normal text-black">{value}</div>
    </div>
  );
}

export default function JobPaymentSummarySection({
  summary,
  variant,
  showBilling = true,
  showOpsDebug = false,
  className = '',
}: JobPaymentSummarySectionProps) {
  const { t } = useTranslation();

  const billingLabel = formatBillingLabel(summary, t);
  const billingAmount =
    summary.paymentType === 'fixed'
      ? null
      : summary.hourlyRate != null
        ? `$${summary.hourlyRate}`
        : null;

  return (
    <div
      className={`grid lg:grid-cols-5 md:grid-cols-4 grid-cols-2 w-full gap-2 gap-y-4 text-sm font-semibold ${className}`}
    >
      {showBilling && (
        <>
          <SummaryField label={t('billingModel')} value={billingLabel} />
          {summary.paymentType === 'hourly' && (
            <>
              <SummaryField
                label={t('hourlyRate')}
                value={billingAmount ?? '—'}
              />
              <SummaryField
                label={t('totalHours')}
                value={summary.totalHours ?? '—'}
              />
            </>
          )}
        </>
      )}

      {variant === 'client' && (
        <>
          <SummaryField
            label={t('clientPaymentMethodLabel')}
            value={formatPaymentMethod(summary.clientPaymentMethod, t)}
          />
          <SummaryField
            label={t('clientPaymentStatus')}
            value={formatClientPaymentStatus(summary.clientPaymentStatus, t)}
          />
          <SummaryField
            label={t('workerPaymentMethodLabel')}
            value={formatPaymentMethod(summary.workerPaymentMethod, t)}
          />
        </>
      )}

      {variant === 'worker' && (
        <SummaryField
          label={t('workerReceivesVia')}
          value={formatPaymentMethod(summary.workerPaymentMethod, t)}
        />
      )}

      {showOpsDebug && summary.paymentFlow !== 'UNKNOWN' && (
        <SummaryField label={t('paymentFlow')} value={summary.paymentFlow} />
      )}
    </div>
  );
}
